// src/services/azureProjectImagesService.js
// Azure Table Storage Service für Bild-Metadaten

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectImages",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// Validierung
const validateConfig = () => {
  if (!AZURE_CONFIG.storageAccount) {
    throw new Error("Azure Storage Account nicht konfiguriert");
  }
  if (!AZURE_CONFIG.tableName) {
    throw new Error("Table Name nicht konfiguriert");
  }
  if (!AZURE_CONFIG.sasToken) {
    throw new Error("SAS Token für Table Storage nicht konfiguriert");
  }
};

// Bild-Metadaten speichern
export const saveImage = async (metadata) => {
  try {
    console.log("Speichere Metadaten:", metadata.bildId);

    validateConfig();

    if (!metadata.projectId) {
      throw new Error("Projekt-ID fehlt");
    }
    if (!metadata.bildId) {
      throw new Error("Bild-ID fehlt");
    }

    const entity = {
      PartitionKey: metadata.projectId,
      RowKey: metadata.bildId,
      Kategorie: metadata.kategorie || "",
      Bildtext: metadata.bildtext || "",
      IsPublic: metadata.isPublic || false,
      Type: metadata.type || "image", // NEU
      Urls: JSON.stringify(metadata.urls),
      OriginalFileName: metadata.originalFileName || "",
      FileSize: metadata.fileSize || 0,
      CreatedAt: metadata.createdAt || new Date().toISOString(),
      UpdatedAt: new Date().toISOString(),
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Table Save Error:", errorText);

      if (response.status === 404) {
        throw new Error(
          `Table '${AZURE_CONFIG.tableName}' nicht gefunden. Bitte im Azure Portal erstellen.`
        );
      } else if (response.status === 403) {
        throw new Error(
          "Zugriff verweigert. SAS Token für Table Storage ist ungültig oder abgelaufen."
        );
      } else if (response.status === 409) {
        throw new Error("Bild-ID existiert bereits. Bitte erneut versuchen.");
      }

      throw new Error(
        `Speichern fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    console.log("Metadaten erfolgreich gespeichert");
    return metadata;
  } catch (error) {
    console.error("Save Image Metadata Error:", error);
    throw new Error(
      `Metadaten konnten nicht gespeichert werden: ${error.message}`
    );
  }
};

// Alle Bilder eines Projekts laden
export const getProjectImages = async (projectId, kategorie = null) => {
  try {
    console.log("Lade Bilder:", { projectId, kategorie });

    validateConfig();

    if (!projectId) {
      throw new Error("Projekt-ID fehlt");
    }

    let filterQuery = `PartitionKey eq '${projectId}'`;
    if (kategorie) {
      filterQuery += ` and Kategorie eq '${kategorie}'`;
    }

    const url = `https://${
      AZURE_CONFIG.storageAccount
    }.table.core.windows.net/${AZURE_CONFIG.tableName}?${
      AZURE_CONFIG.sasToken
    }&$filter=${encodeURIComponent(filterQuery)}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Table Load Error:", errorText);

      if (response.status === 404) {
        throw new Error(`Table '${AZURE_CONFIG.tableName}' nicht gefunden.`);
      } else if (response.status === 403) {
        throw new Error("Zugriff verweigert. SAS Token ist ungültig.");
      }

      throw new Error(
        `Laden fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    const data = await response.json();

    const images = data.value
      .map((item) => {
        try {
          const urls = JSON.parse(item.Urls || "{}");
          console.log("Geladene URLs für", item.RowKey, ":", urls);

          return {
            bildId: item.RowKey,
            projectId: item.PartitionKey,
            kategorie: item.Kategorie,
            bildtext: item.Bildtext || "",
            isPublic: item.IsPublic || false,
            type: item.Type || "image",
            urls: urls,
            originalFileName: item.OriginalFileName || "",
            fileSize: item.FileSize || 0,
            createdAt: item.CreatedAt,
            updatedAt: item.UpdatedAt,
          };
        } catch (error) {
          console.error("Fehler beim Parsen von Bild-Metadaten:", item, error);
          return null;
        }
      })
      .filter((img) => img !== null);

    console.log(`${images.length} Bilder geladen`);
    return images;
  } catch (error) {
    console.error("Load Images Error:", error);
    return [];
  }
};

// Einzelnes Bild laden
export const getImage = async (projectId, bildId) => {
  try {
    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${projectId}',RowKey='${bildId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      headers: { Accept: "application/json;odata=nometadata" },
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Laden fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    const item = await response.json();

    return {
      bildId: item.RowKey,
      projectId: item.PartitionKey,
      kategorie: item.Kategorie,
      bildtext: item.Bildtext || "",
      isPublic: item.IsPublic || false,
      urls: JSON.parse(item.Urls || "{}"),
      originalFileName: item.OriginalFileName || "",
      fileSize: item.FileSize || 0,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    };
  } catch (error) {
    console.error("Load Image Error:", error);
    return null;
  }
};

// Bildtext und isPublic aktualisieren
export const updateImage = async (projectId, bildId, bildtext, isPublic) => {
  try {
    console.log("Update Metadaten:", { projectId, bildId, bildtext, isPublic });

    validateConfig();

    if (!projectId) {
      throw new Error("Projekt-ID fehlt");
    }
    if (!bildId) {
      throw new Error("Bild-ID fehlt");
    }

    // Erst bestehendes Bild laden
    const existing = await getImage(projectId, bildId);
    if (!existing) {
      throw new Error("Bild nicht gefunden");
    }

    // Vollständige Entity mit allen Feldern (PUT braucht alle)
    const entity = {
      PartitionKey: projectId,
      RowKey: bildId,
      Kategorie: existing.kategorie,
      Bildtext: bildtext || "",
      IsPublic: isPublic || false,
      Urls: JSON.stringify(existing.urls),
      OriginalFileName: existing.originalFileName,
      FileSize: existing.fileSize,
      CreatedAt: existing.createdAt,
      UpdatedAt: new Date().toISOString(),
    };

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${projectId}',RowKey='${bildId}')?${AZURE_CONFIG.sasToken}`;

    console.log("Update URL (ohne Token):", url.split("?")[0]);

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Update Error Response:", response.status, errorText);

      if (response.status === 404) {
        throw new Error("Bild nicht gefunden in Datenbank");
      } else if (response.status === 403) {
        throw new Error(
          "Zugriff verweigert. SAS Token für Table Storage ist ungültig oder hat keine Update-Berechtigung."
        );
      }

      throw new Error(
        `Aktualisierung fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    console.log("Metadaten erfolgreich aktualisiert");
    return true;
  } catch (error) {
    console.error("Update Image Error Details:", error);

    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      throw new Error(
        "CORS-Fehler für Table Storage. Prüfe: CORS für Table service aktiviert mit GET, PUT, POST, DELETE, OPTIONS"
      );
    }

    throw new Error(`Aktualisierung fehlgeschlagen: ${error.message}`);
  }
};

// Bild löschen
export const deleteImage = async (projectId, bildId) => {
  try {
    console.log("Lösche Metadaten:", { projectId, bildId });

    validateConfig();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='${projectId}',RowKey='${bildId}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(
        `Löschen fehlgeschlagen (${response.status}): ${errorText}`
      );
    }

    console.log("Metadaten erfolgreich gelöscht");
    return true;
  } catch (error) {
    console.error("Delete Image Error:", error);
    throw new Error(`Löschen fehlgeschlagen: ${error.message}`);
  }
};

// Alle Bilder eines Projekts löschen
export const deleteAllProjectImages = async (projectId) => {
  try {
    console.log("Lösche alle Bilder von Projekt:", projectId);

    const images = await getProjectImages(projectId);

    await Promise.all(
      images.map((image) => deleteImage(projectId, image.bildId))
    );

    console.log(`${images.length} Bilder gelöscht`);
    return true;
  } catch (error) {
    console.error("Delete All Images Error:", error);
    throw new Error(`Löschen aller Bilder fehlgeschlagen: ${error.message}`);
  }
};
